import {
  Box,
  Flex,
  HStack,
  useDisclosure,
  VStack,
  useColorModeValue,
  CloseButton,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { GetLoanProductsAndAssociatedFacilitiesForCreditFacilityQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { UploadReceivablesFileForm } from '../UploadReceivablesFileForm';
import {
  ReceivableUpload,
  LoanProductCreditFacilityReceivableUploadMap,
  UploadState,
  UploadFileModal,
  UploadFileErrorAlert,
  initialUploadState,
  ActionsDrawerState,
  ActionsDrawerShell,
  ActionsDrawerHeader,
  ActionsDrawerFooter,
} from './shared';
import { Select } from 'packages/common-frontend/components/Select';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';

export const UploadTransferFileInput = ({
  receivableUpload,
  validateFileStructure,
  setReceivableUploadsMap,
  kickingOffJob,
  creditFacilityOptions,
}: {
  receivableUpload: ReceivableUpload;
  validateFileStructure: (
    file: File,
    receivableUpload: ReceivableUpload,
  ) => Promise<void>;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  kickingOffJob: boolean;
  creditFacilityOptions: { value: string; label: string }[];
}) => {
  const uploadFileModal = useDisclosure();

  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');

  finleyAssert(
    creditFacilityOptions.length > 0,
    `No credit facilities available for transfer`,
  );

  useEffect(() => {
    if (
      !receivableUpload.creditFacilityId &&
      creditFacilityOptions.length === 1
    ) {
      setReceivableUploadsMap((receivableUploadsMap) => {
        return {
          ...receivableUploadsMap,
          [receivableUpload.key]: {
            ...receivableUploadsMap[receivableUpload.key],
            creditFacilityId: creditFacilityOptions[0].value,
          },
        };
      });
    }
  }, [
    creditFacilityOptions,
    receivableUpload.creditFacilityId,
    receivableUpload.key,
    setReceivableUploadsMap,
  ]);

  return (
    <Flex
      direction="column"
      width="100%"
      mb={receivableUpload.fileError ? '0px' : '40px'}
    >
      <Typography TextProps={{ fontSize: '16px', fontWeight: 600 }}>
        {receivableUpload.loanProductName}
      </Typography>
      <VStack
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${borderColor}`}
        bg={backgroundColor}
        mt="24px"
      >
        <HStack width="100%" justifyContent="space-between">
          <Typography TextProps={{ fontWeight: 500 }}>Facility</Typography>
          <UploadState
            receivableUpload={receivableUpload}
            openUploadModal={() => uploadFileModal.onOpen()}
            kickingOffJob={kickingOffJob}
          />
          <UploadFileModal
            isOpen={uploadFileModal.isOpen}
            onClose={uploadFileModal.onClose}
          >
            <UploadReceivablesFileForm
              onClose={uploadFileModal.onClose}
              handleFile={(file) => {
                validateFileStructure(file, receivableUpload);
              }}
            />
          </UploadFileModal>
        </HStack>
        <Flex
          mt="10px"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          {receivableUpload.creditFacilityId &&
          creditFacilityOptions.length === 1 ? (
            <Typography variant="base-muted" TextProps={{ fontSize: '14px' }}>
              {creditFacilityOptions[0].label}
            </Typography>
          ) : (
            <Box width="50%">
              <Select
                options={creditFacilityOptions}
                value={receivableUpload.creditFacilityId}
                onChange={(newCreditFacilityId?: string) => {
                  setReceivableUploadsMap((receivableUploadsMap) => {
                    return {
                      ...receivableUploadsMap,
                      [receivableUpload.key]: {
                        ...receivableUploadsMap[receivableUpload.key],
                        creditFacilityId: newCreditFacilityId,
                      },
                    };
                  });
                }}
                placeholderLabel="Select facility"
                buttonProps={{ background: 'white' }}
              />
            </Box>
          )}

          {receivableUpload.file && (
            <Flex alignItems="center">
              <Typography
                variant="base-muted"
                TextProps={{ fontSize: '14px', mr: '8px' }}
              >
                {receivableUpload.file?.name}
              </Typography>
              <CloseButton
                onClick={() => {
                  setReceivableUploadsMap((receivableUploadsMap) => {
                    return {
                      ...receivableUploadsMap,
                      [receivableUpload.key]: {
                        ...receivableUploadsMap[receivableUpload.key],
                        ...initialUploadState,
                      },
                    };
                  });
                }}
                colorScheme="gray"
                isDisabled={kickingOffJob}
              />
            </Flex>
          )}
        </Flex>
      </VStack>
      {receivableUpload.fileError && <UploadFileErrorAlert />}
    </Flex>
  );
};

export const TransferDrawer = ({
  onClose,
  receivableUploadsMap,
  setReceivableUploadsMap,
  creditFacilityData,
  currentCreditFacilityId,
  validateFileStructure,
  kickingOffJob,
  initiateReassignmentConfirmationJob,
}: {
  onClose: () => void;
  receivableUploadsMap: LoanProductCreditFacilityReceivableUploadMap;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  creditFacilityData: GetLoanProductsAndAssociatedFacilitiesForCreditFacilityQueryGQLType;
  currentCreditFacilityId: string;
  validateFileStructure: (
    file: File,
    receivableUpload: ReceivableUpload,
  ) => Promise<void>;
  kickingOffJob: boolean;
  initiateReassignmentConfirmationJob: (
    jobType: ActionsDrawerState,
  ) => Promise<void>;
}) => {
  const submitDisabled = Object.values(receivableUploadsMap).some(
    (upload) => !upload.isValid || !upload.file || !upload.creditFacilityId,
  );

  return (
    <ActionsDrawerShell onClose={onClose}>
      <ActionsDrawerHeader onClose={onClose} actionsDrawerState="transfer" />
      <Flex p="40px" direction="column" justifyContent="space-between" h="100%">
        <Flex direction="column">
          <Typography variant="base-muted">
            Select a facility and upload a headerless CSV file containing a
            single column of Receivable IDs for transfer.
          </Typography>
          <Flex mt="40px" direction="column" gap="24px">
            <VStack spacing="8px" alignItems="flex-start" width="100%">
              {Object.values(receivableUploadsMap).map((receivableUpload) => {
                const receivableUploadLoanProduct = creditFacilityData.creditFacility.loanProducts.find(
                  (lp) => lp.id === receivableUpload.loanProductId,
                );
                finleyAssert(
                  receivableUploadLoanProduct &&
                    receivableUploadLoanProduct.currentCreditFacilities,
                  `Unable to find loan product and associated credit facilities for file upload`,
                );
                return (
                  <UploadTransferFileInput
                    key={receivableUpload.key}
                    validateFileStructure={validateFileStructure}
                    receivableUpload={receivableUpload}
                    setReceivableUploadsMap={setReceivableUploadsMap}
                    kickingOffJob={kickingOffJob}
                    creditFacilityOptions={receivableUploadLoanProduct.currentCreditFacilities
                      .filter((cf) => cf.id !== currentCreditFacilityId)
                      .map((cf) => {
                        return { value: cf.id, label: cf.name };
                      })}
                  />
                );
              })}
            </VStack>
          </Flex>
        </Flex>
        <ActionsDrawerFooter
          onSubmit={async () => {
            await initiateReassignmentConfirmationJob('transfer');
            onClose();
          }}
          kickingOffJob={kickingOffJob}
          submitDisabled={submitDisabled}
        />
      </Flex>
    </ActionsDrawerShell>
  );
};
